import { Component, Input, OnChanges, computed, inject, signal } from '@angular/core';
import { AuthService } from '../core/auth.service';
import { I18nService } from '../core/i18n.service';
import { SellerFanService } from '../core/seller-fan.service';

@Component({
  selector: 'app-seller-fan-button',
  standalone: true,
  template: `
    @if (sellerID > 0) {
      <button type="button" class="fan-button" [class.is-fan]="state().following" [class.compact]="compact"
        [disabled]="busy()" [attr.aria-pressed]="state().following"
        [attr.aria-label]="busy() ? copy().saving : (state().following ? copy().following : copy().follow)"
        [attr.title]="state().following ? copy().following : copy().follow" (click)="toggle($event)">
        <span aria-hidden="true">{{ state().following ? '♥' : '♡' }}</span>
      </button>
    }
  `,
  styles: [`
    :host{display:inline-block;height:38px;width:38px}.fan-button{align-items:center;background:color-mix(in srgb,var(--surface,#fff) 94%,transparent);border:1px solid color-mix(in srgb,var(--ink,#211d20) 22%,var(--border,#ddd));border-radius:50%;box-shadow:0 5px 16px rgb(24 15 20 / 16%);color:var(--ink,#211d20);cursor:pointer;display:grid;font:inherit;height:38px;padding:0;place-items:center;transition:background .18s ease,border-color .18s ease,color .18s ease,transform .18s ease;width:38px}.fan-button:hover{background:var(--surface,#fff);border-color:var(--accent,#df4b95);color:var(--accent,#b52b70);transform:scale(1.06)}.fan-button.is-fan{background:var(--accent,#df4b95);border-color:var(--accent,#df4b95);color:var(--accent-contrast,#fff)}.fan-button:disabled{cursor:wait;opacity:.65}.fan-button span{font-size:1.35rem;line-height:1;transform:translateY(-1px)}:host:has(.compact),.fan-button.compact{height:34px;width:34px}.fan-button.compact span{font-size:1.18rem}
  `]
})
export class SellerFanButtonComponent implements OnChanges {
  @Input() sellerID = 0;
  @Input() followerCount = 0;
  @Input() showCount = true;
  @Input() compact = false;

  private readonly auth = inject(AuthService);
  private readonly fans = inject(SellerFanService);
  private readonly i18n = inject(I18nService);
  protected readonly busy = signal(false);
  protected readonly state = computed(() => this.fans.state(this.sellerID, this.followerCount));
  protected readonly copy = computed(() => ({
    tr:{follow:'Hayran ol',following:'Hayranısın',saving:'Kaydediliyor…',fans:'hayran'},
    en:{follow:'Become a fan',following:'You’re a fan',saving:'Saving…',fans:'fans'},
    de:{follow:'Fan werden',following:'Du bist Fan',saving:'Speichern…',fans:'Fans'},
    es:{follow:'Hazte fan',following:'Eres fan',saving:'Guardando…',fans:'fans'},
    fr:{follow:'Devenir fan',following:'Vous êtes fan',saving:'Enregistrement…',fans:'fans'}
  } as Record<string,{follow:string;following:string;saving:string;fans:string}>)[this.i18n.lang()] || ({follow:'Become a fan',following:'You’re a fan',saving:'Saving…',fans:'fans'}));

  ngOnChanges(): void {
    if (this.sellerID > 0 && this.auth.loggedIn()) void this.fans.ensure(this.sellerID, this.followerCount);
  }

  protected async toggle(event: MouseEvent): Promise<void> {
    event.preventDefault();
    event.stopPropagation();
    if (!this.auth.loggedIn()) {
      this.auth.requestLogin('login');
      return;
    }
    this.busy.set(true);
    try {
      const current = await this.fans.ensure(this.sellerID, this.followerCount);
      const next=await this.fans.toggle(this.sellerID, !current.following, this.followerCount);
      if(typeof window!=='undefined')window.dispatchEvent(new CustomEvent('aymudo:seller-favorite-changed',{detail:{sellerID:this.sellerID,following:next.following}}));
    } finally {
      this.busy.set(false);
    }
  }
}
